import Component from '../../js/_Component';

const CNAME = 'c-block-masonry';

interface Column {
	/** The column element. */
	element: HTMLElement;

	/** Whether or not this column is available to host items. */
	active: boolean;

	/** The items which are being staged to enter this column. */
	items: HTMLElement[];

	/** The total height of the items which are currently staged to enter this column. */
	height: number;
}

interface Item {
	/** The item element. */
	element: HTMLElement;

	/** The item height, assuming a width of 1 (actualHeight / actualWidth). */
	height: number;
}

export default class BlockMasonry extends Component {
	/** The array of columns. */
	private columns: Column[];

	/** The array of items. */
	private items: Item[];

	constructor(element: HTMLElement) {
		super(element);

		// Get the columns and items.
		const columns = this.element.querySelectorAll(`.${CNAME}--column`);
		const items = this.element.querySelectorAll(`.${CNAME}--item`);

		// Store the data for each column and item.
		this.columns = Array.from(columns).map((column) => BlockMasonry.getColumnInterface(column));
		this.items = Array.from(items).map((item) => BlockMasonry.getItemInterface(item));

		// Calculate layout initially and upon window resize.
		this.setLayout();
		this.element.addEventListener('resizeclassesapplied', () => this.onResize());
	}

	static getColumnInterface(element) {
		// Store the HTMLElement, visibility status, staged items, and staged height.
		return {
			element,
			active: window.getComputedStyle(element).display !== 'none',
			items: [],
			height: 0,
		};
	}

	static getItemInterface(element) {
		// Store the HTMLElement and height.
		return {
			element,
			height: Number(element.dataset.height),
		};
	}

	getActiveColumns() {
		// Filter columns by whether or not they are visible.
		return this.columns.filter((column) => column.active);
	}

	getShortestActiveColumnIndex() {
		// Map each column to its height, where inactive columns are invalidated by using infinity.
		const heights = this.columns.map((column) => (column.active ? column.height : Infinity));

		// Return the index of the column with the minimum height.
		return heights.indexOf(Math.min(...heights));
	}

	onResize() {
		// Check the number of active columns before and after.
		const oldColumns = this.getActiveColumns();
		this.columns = this.columns.map((column) => BlockMasonry.getColumnInterface(column.element));
		const newColumns = this.getActiveColumns();

		// Only set layout if there is a different number of active columns than before.
		if (oldColumns.length !== newColumns.length) {
			this.setLayout();
		}
	}

	setLayout() {
		// Perform all of the calculation virtually, without affecting the DOM.
		this.items.forEach((item) => {
			const shortestColumn = this.columns[this.getShortestActiveColumnIndex()];
			shortestColumn.items.push(item.element);
			shortestColumn.height += item.height;
		});

		// Commit changes to the DOM by appending each item into its assigned column.
		this.getActiveColumns().forEach((column) => {
			column.items.forEach((item) => {
				column.element.appendChild(item);
			});
		});
	}
}

Component.register(BlockMasonry, 'BlockMasonry');
